// @ts-nocheck
import React from 'react';
import intl from 'react-intl-universal';
import { MenuItem, Button } from '@blueprintjs/core';
import { Suggest } from '@blueprintjs/select';
import { FormattedMessage as T } from '@/components';
import classNames from 'classnames';
import { CLASSES } from '@/constants/classes';

/**
 * Fund suggest field.
 * @returns {JSX.Element}
 */
export function FundSuggestField({
  funds,
  initialFundId,
  selectedFundId,
  defaultSelectText = intl.get('select_fund'),
  popoverFill = false,
  onFundSelected,
  ...suggestProps
}) {
  const initialFund = React.useMemo(
    () => funds.find((f) => f.id === initialFundId),
    [initialFundId, funds],
  );

  const [selectedFund, setSelectedFund] = React.useState(initialFund || null);

  React.useEffect(() => {
    if (typeof selectedFundId !== 'undefined') {
      const fund = selectedFundId
        ? funds.find((f) => f.id === selectedFundId)
        : null;
      setSelectedFund(fund);
    }
  }, [selectedFundId, funds, setSelectedFund]);

  /**
   * Renders the fund item.
   */
  const fundItemRenderer = (fund, { handleClick, modifiers, query }) => {
    return (
      <MenuItem
        disabled={modifiers.disabled}
        label={fund.code}
        key={fund.id}
        onClick={handleClick}
        text={fund.name}
      />
    );
  };

  /**
   * Predicate for filtering funds.
   */
  const fundItemPredicate = (query, fund, _index, exactMatch) => {
    const normalizedTitle = fund.name.toLowerCase();
    const normalizedQuery = query.toLowerCase();

    if (exactMatch) {
      return normalizedTitle === normalizedQuery;
    } else {
      return `${fund.code}. ${normalizedTitle}`.indexOf(normalizedQuery) >= 0;
    }
  };

  /**
   * Handles fund selection.
   */
  const fundItemSelect = React.useCallback(
    (fund) => {
      if (fund.id) {
        setSelectedFund({ ...fund });
        onFundSelected && onFundSelected(fund);
      }
    },
    [setSelectedFund, onFundSelected],
  );

  /**
   * Renders the input value.
   */
  const fundInputValueRenderer = (inputValue) => {
    if (inputValue) {
      return inputValue.name.toString();
    }
    return '';
  };

  return (
    <Suggest
      items={funds}
      noResults={<MenuItem disabled={true} text={<T id={'no_funds'} />} />}
      itemRenderer={fundItemRenderer}
      itemPredicate={fundItemPredicate}
      onItemSelect={fundItemSelect}
      selectedItem={selectedFund}
      inputProps={{
        placeholder: defaultSelectText,
        rightElement:
          selectedFund && (
            <Button
              minimal
              icon="cross"
              aria-label={intl.get('clear')}
              onClick={(e) => {
                e.stopPropagation();
                setSelectedFund(null);
                onFundSelected && onFundSelected(null);
              }}
            />
          ),
      }}
      resetOnClose={true}
      fill={true}
      popoverProps={{ minimal: true, boundary: 'window' }}
      inputValueRenderer={fundInputValueRenderer}
      className={classNames(CLASSES.FORM_GROUP_LIST_SELECT, {
        [CLASSES.SELECT_LIST_FILL_POPOVER]: popoverFill,
      })}
      {...suggestProps}
    />
  );
} 